import React, { Fragment, useCallback, useMemo, useState } from "react"
import { makeStyles } from "@material-ui/core/styles"
import PropTypes from "prop-types"
import Button from "@material-ui/core/Button"
import Dialog from "@material-ui/core/Dialog"
import DialogActions from "@material-ui/core/DialogActions"
import DialogContent from "@material-ui/core/DialogContent"
import Slide from "@material-ui/core/Slide"
import IconButton from "@material-ui/core/IconButton"
import ScanIcon from "@material-ui/icons/FilterCenterFocus"
import BorderAllIcon from "@material-ui/icons/BorderAll"
import ClearIcon from "@material-ui/icons/Close"
import TextField from "@material-ui/core/TextField"
import InputAdornment from "@material-ui/core/InputAdornment"
import ReactQuagga, { useQuagga } from "../ReactQuagga"
import { isHex } from "../../utils/validation"
import API, { graphqlOperation } from "@aws-amplify/api"
import { getMasterLocationByMasterId } from "../../graphql/queries"
import Tooltip from "@material-ui/core/Tooltip"
import ReactQrReader from "../ReactQrReader"
// import api from "../../constants/api"  used for PM-758

const Transition = React.forwardRef(function Transition(props, ref) {
	return <Slide direction="up" ref={ref} {...props} />
})

const useStyles = makeStyles((theme) => ({
	content: {
		display: "flex",
		alignItems: "center",
		flexDirection: "column",
		marginTop: theme.spacing(1),
		overflow: "hidden",
	},
	actions: {
		justifyContent: "space-between",
	},
	actionButton: {
		margin: theme.spacing(1),
		flex: 1,
	},
	paper: {
		padding: "2px 4px",
		display: "flex",
		width: "100%",
	},
	fabClose: {
		position: "fixed",
		bottom: 0,
		right: 0,
		margin: theme.spacing(2),
		zIndex: 500,
	},
	fabText: {
		position: "fixed",
		bottom: 0,
		right: 40 + 24,
		margin: theme.spacing(2),
		zIndex: 500,
	},
}))

const DeviceIdInput = ({
	handleClose,
	handleAccept,
	locIds,
	scannerSupported,
	deviceIdEntryMethod,
	setDeviceIdEntryMethod,
}) => {
	const [deviceid, setDeviceId] = useState(null)
	const [loading, setLoading] = useState(false)
	const classes = useStyles()
	// TODO: This isn't working. Presumably appears underneath other elements, so we can't see the snackbar.
	//  Fix and replace alerts with snackbars.
	// const { enqueueSnackbar } = useSnackbar()
	// TODO uncomment and use this function once PM-758 is complete
	// const handleNodeBoardScan = useCallback(
	// 	(data) => {
	// 		data["node"] = "f739a2cc-e178-484e-af18-665e83448a7a" // PM-758, need Node UUID's here
	// 		data["_part_number"] = data["Board Rev"]
	// 		delete data["Board Rev"]
	// 		data["replaced_by"] = api.cognitoID
	// 		data["original_radio_id"] = data["radio_id"]
	// 		delete data["radio_id"]
	// 		console.log("JSON to API", data)
	// 		api.setNodeBoard(data["node"], data).then((response) => {
	// 			console.log("API Response", response)
	// 		})
	// 	},
	// 	[],
	// )

	const handleChange = useCallback(
		(e) => {
			let newValue = e.target.value
			if (newValue) {
				// Remove all non-hex characters
				newValue = newValue.replace("0x", "").toUpperCase()
				newValue = newValue.replace(/[^A-F0-9]/g, "")
			}
			setDeviceId(newValue)
			setDeviceIdEntryMethod("text")
		},
		[setDeviceIdEntryMethod],
	)

	const locIdsSplit = useMemo(() => {
		if (locIds.length) {
			return locIds.split(":")
		}
		return []
	}, [locIds])

	let label = null
	if (locIdsSplit.length === 2) {
		label = "Master"
	} else if (locIdsSplit.length === 3) {
		label = "Node"
	}

	const error = useMemo(() => {
		if (deviceid) {
			const isValid = isHex(deviceid)
			if (!isValid) {
				return "Not a valid hex ID."
			}
			if (deviceid.length > 16) {
				return "Max 16 characters."
			}
			if (locIdsSplit.length === 2 && deviceid.length !== 16) {
				return "MasterID must be length of 16"
			}
		}
	}, [deviceid, locIdsSplit.length])

	const isValid = useMemo(() => {
		if (error) return false
		if (!deviceid) return false
		if (deviceid.length > 16) return false
		if (deviceid.length > 8) {
			// TODO: (Magnus - 3/31/2023) I don't understand the purpose of this, but an appropriate message should be
			//  returned in the `error` memo.
			// ABCD1234 -> 0000ABCD, 00001234
			const firstHalf = deviceid.slice(0, 8).padStart(8, "0")
			const secondHalf = deviceid.slice(8, 16).padStart(8, "0")
			if (
				parseInt(firstHalf, 16).toString(16).toUpperCase().padStart(8, "0") !== firstHalf ||
				parseInt(secondHalf, 16).toString(16).toUpperCase().padStart(8, "0") !== secondHalf
			)
				return false
		} else {
			if (parseInt(deviceid, 16).toString(16).toUpperCase().padStart(8, "0") !== deviceid.padStart(8, "0")) return false
		}
		return true
	}, [deviceid, error])

	const handleSubmit = useCallback(
		async (e) => {
			e.preventDefault()
			if (isValid) {
				setLoading(true)
				try {
					// The user may be offline. If so, graphQL queries will not work. At the same time, we need to avoid duplicate
					// master IDs. For now, we will set a timeout on the check and allow the user to proceed even if we haven't
					// confirmed that there are no duplicates.
					const deviceIdAlreadyInUsePromise = new Promise((resolve) => {
						API.graphql(graphqlOperation(getMasterLocationByMasterId, { mId: deviceid }))
							.then((result) => {
								const items = result.data.getMasterLocationByMasterId.items
								resolve(!!items && !!items.length)
							})
							.catch((error) => {
								console.error(error)
								resolve("no-network")
							})
					})
					const timeoutPromise = new Promise((resolve) => setTimeout(resolve, 5000, "timeout"))

					await Promise.race([deviceIdAlreadyInUsePromise, timeoutPromise]).then(async (result) => {
						if (result === "timeout" || result === "no-network") {
							console.warn(
								"Unable to confirm that this ID is not in use because you are not connected to the internet. " +
									"Proceeding anyway.",
							)
							handleAccept(deviceid)
						} else {
							if (result === true) {
								alert(`Master ID ${deviceid} is already in use.`)
								setDeviceId("")
							} else {
								handleAccept(deviceid)
							}
						}
					})
				} finally {
					setLoading(false)
				}
			}
		},
		[deviceid, handleAccept, isValid],
	)

	const handleBarCodeDetection = useCallback(
		(data) => {
			// Create a synthetic event object, so we can reuse the logic in handleChange for scans
			const e = {
				target: { value: data.codeResult.code },
			}
			handleChange(e)
		},
		[handleChange],
	)
	const handleQrCodeDetection = useCallback(
		(data) => {
			// Create a synthetic event object, so we can reuse the logic in handleChange for scans
			const e = {
				target: { value: data["radio_id"] },
			}
			// Old functionality for updating the Radio ID in the old system with DynamoDB
			handleChange(e)
			// let copy_data = { ...data }
			// implement sending Node Board Scans to the REST API
			// handleNodeBoardScan(copy_data).then()
		},
		[handleChange],
	)

	const handleCloseTest = useCallback(() => {
		setDeviceIdEntryMethod("text")
	}, [setDeviceIdEntryMethod])

	let endAdornment = null
	if (deviceid) {
		endAdornment = (
			<InputAdornment position="end">
				<IconButton edge="end" aria-label="Clear" onClick={() => setDeviceId(null)}>
					<ClearIcon />
				</IconButton>
			</InputAdornment>
		)
	} else if (scannerSupported) {
		endAdornment = (
			<InputAdornment position="end">
				<IconButton edge="end" aria-label="Scan" onClick={() => setDeviceIdEntryMethod("scan")}>
					<Tooltip title="Barcode Scan">
						<ScanIcon />
					</Tooltip>
				</IconButton>
				<IconButton edge="end" aria-label="Scan" onClick={() => setDeviceIdEntryMethod("qr_scan")}>
					<Tooltip title="QR Code Scan">
						<BorderAllIcon />
					</Tooltip>
				</IconButton>
			</InputAdornment>
		)
	}

	let helperText
	if (error) {
		helperText = error
	} else if (deviceid !== null && !isValid) {
		helperText = `Invalid ${label} ID`
	} else {
		helperText = `${label} ID - no leading '0x'`
	}

	return (
		<Fragment>
			<form onSubmit={handleSubmit} action="#">
				<DialogContent id="device-id-input-description" className={classes.content}>
					{deviceIdEntryMethod === "text" ? (
						<TextField
							id="deviceid-input"
							variant="outlined"
							className={classes.paper}
							type="text"
							label={`${label} ID`}
							onChange={handleChange}
							value={deviceid || ""}
							error={!!deviceid && !isValid}
							helperText={helperText}
							autoFocus={deviceIdEntryMethod === "text" || !scannerSupported}
							InputProps={{
								endAdornment,
							}}
						/>
					) : deviceIdEntryMethod === "scan" ? (
						<ReactQuagga
							onDetected={handleBarCodeDetection}
							onClose={handleCloseTest}
							onError={(err) => {
								if (err.message.includes("denied")) {
									alert("Camera permission denied")
									setDeviceIdEntryMethod("text")
								}
							}}
						/>
					) : (
						<ReactQrReader
							onDetected={handleQrCodeDetection}
							onClose={handleCloseTest}
							onError={(err) => {
								if (err.message.includes("denied")) {
									alert("Camera permission denied")
									setDeviceIdEntryMethod("text")
								}
							}}
						/>
					)}
				</DialogContent>
				{deviceIdEntryMethod === "text" && (
					<DialogActions className={classes.actions}>
						<Button onClick={handleClose} className={classes.actionButton}>
							Close
						</Button>
						<Button
							variant="contained"
							color="primary"
							type="submit"
							disabled={!isValid || loading}
							className={classes.actionButton}
						>
							Accept
						</Button>
					</DialogActions>
				)}
			</form>
		</Fragment>
	)
}

DeviceIdInput.propTypes = {
	handleClose: PropTypes.func.isRequired,
	deviceIdEntryMethod: PropTypes.string,
	setDeviceIdEntryMethod: PropTypes.func,
	locIds: PropTypes.string.isRequired,
	handleAccept: PropTypes.func.isRequired,
}

const useStylesDialog = makeStyles((theme) => ({
	dialogPaper: {
		margin: theme.spacing(1),
	},
	dialogWidth: {
		width: "calc(100% - 16px)",
	},
}))

const DialogDeviceIdInput = (props) => {
	const [deviceIdEntryMethod, setDeviceIdEntryMethod] = useState("text")
	const classes = useStylesDialog()
	const scannerSupported = useQuagga()
	const isOpen = props.locIds.length > 0

	return (
		<Dialog
			open={isOpen}
			TransitionComponent={Transition}
			keepMounted={false}
			onClose={props.handleClose}
			aria-labelledby="device-id-input-title"
			aria-describedby="device-id-input-description"
			fullWidth={true}
			classes={{
				paper: classes.dialogPaper,
				paperFullWidth: classes.dialogWidth,
			}}
		>
			{isOpen && (
				<DeviceIdInput
					scannerSupported={scannerSupported}
					deviceIdEntryMethod={deviceIdEntryMethod}
					setDeviceIdEntryMethod={setDeviceIdEntryMethod}
					{...props}
				/>
			)}
		</Dialog>
	)
}

DialogDeviceIdInput.propTypes = DeviceIdInput.propTypes

export default DialogDeviceIdInput
